import { useEffect, type RefObject } from "react";
import useDebounceCallback from "./useDebouncCallback";

type Options = {
  delay?: number;
};

interface UseInViewProps {
  childRef: RefObject<HTMLElement | null>;
  onInView: (inView: boolean) => void;
  options?: Options;
}

const useInView = ({ childRef, onInView, options }: UseInViewProps) => {
  const { delay } = options ?? {};
  const callback = delay ? useDebounceCallback(onInView, delay) : onInView;

  useEffect(() => {
    let prevInView = false;

    const childElement = childRef.current;
    if (!childElement) return;

    const checkInView = () => {
      const rect = childElement.getBoundingClientRect();
      // A result list can shrink after filtering while the user is scrolled
      // below its pagination sentinel. Treat that sentinel as reached too, so
      // the next batch is appended without requiring a scroll back to the top.
      const inView = rect.top < window.innerHeight;

      if (inView !== prevInView) {
        callback(inView);
        prevInView = inView;
      }
    };

    checkInView();
    document.addEventListener("scroll", checkInView);

    return () => {
      document.removeEventListener("scroll", checkInView);
    };
  }, [childRef, callback]);
};

export default useInView;
